import React, { useState } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import { shareSenReport } from "../services/APIClient";

const SENShareModal = ({
  show,
  handleClose,
  className,
  teacherId,
  schoolId,
  expandedViewData, // Receive detailed data from ExpandedSENWatchlistView
}) => {
  const [manualEmails, setManualEmails] = useState([]); // State to store manually entered emails
  const [emailInput, setEmailInput] = useState(""); // State for the email input field
  const [isSharing, setIsSharing] = useState(false);

  const handleAddEmail = () => {
    if (emailInput.trim() && !manualEmails.includes(emailInput.trim())) {
      setManualEmails((prevEmails) => [...prevEmails, emailInput.trim()]);
      setEmailInput(""); // Clear the input field
    }
  };

  const handleRemoveEmail = (email) => {
    setManualEmails((prevEmails) => prevEmails.filter((e) => e !== email));
  };

  const handleConfirmShare = async () => {
    setIsSharing(true);
    try {
      if (manualEmails.length === 0) {
        throw new Error("No valid email addresses found for recipients");
      }

      if (!teacherId) {
        throw new Error("Missing sender teacher ID");
      }

      if (!schoolId) {
        throw new Error("Missing school ID");
      }

      if (!expandedViewData || !expandedViewData.students) {
        throw new Error("No students data available to share");
      }

      // Extract student IDs
      const selectedStudentIds = expandedViewData.students.map(
        (student) => student.id
      );

      const assessmentName = "SEN Watchlist"; // Use a fixed name for the SEN Watchlist
      const assessmentId = "sen-watchlist"; // Use a fixed ID for the SEN Watchlist
      const isDetailedView = true; // Always send detailed view for SEN Watchlist

      // Format the table data as HTML
      const tableDataHtml = `
        <table border="1" style="border-collapse: collapse; width: 100%;">
          <thead>
            <tr>
              <th>Student Name</th>
              <th>Overall Standard Score vs Test</th>
              <th>Class</th>
              <th>SET</th>
              <th>Classroom Support Plan</th>
              <th>Details Of SET Received</th>
              <th>Priority Concerns</th>
              <th>Additional Needs</th>
              <th>Support Plan</th>
              <th>EAL</th>
              <th>Place on Continuum</th>
            </tr>
          </thead>
          <tbody>
            ${expandedViewData.students
              .map(
                (student) => `
              <tr>
                <td>${student.name}</td>
                <td>${student.readingScore || "N/A"}</td>
                <td>${student.classname || "N/A"}</td>
                <td>${student.setname || "N/A"}</td>
                <td>${student.classroomsupportplan ? "Yes" : "No"}</td>
                <td>${(student.setreceived || []).join(", ") || "N/A"}</td>
                <td>${(student.priorityconcerns || []).join(", ") || "N/A"}</td>
                <td>${(student.additionalneeds || []).join(", ") || "N/A"}</td>
                <td>${(student.supportplan || []).join(", ") || "N/A"}</td>
                <td>${student.eal ? "Yes" : "No"}</td>
                <td>${(student.supportcontinuum || []).join(", ") || "N/A"}</td>
              </tr>
            `
              )
              .join("")}
          </tbody>
        </table>
      `;

      console.log("Sharing SEN Watchlist with the following data:", {
        teacherId,
        schoolId,
        assessmentId,
        assessmentName,
        selectedStudentIds,
        recipientEmails: manualEmails,
        isDetailedView,
        tableDataHtml, // Include the table data in the log
      });

      // Call the shareSenReport API
      await shareSenReport(teacherId, selectedStudentIds, manualEmails);

      alert("SEN Watchlist shared successfully!");
      handleClose();
    } catch (error) {
      console.error("Error sharing SEN Watchlist:", error);
      alert(`Failed to share SEN Watchlist: ${error.message}`);
    } finally {
      setIsSharing(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Share SEN Watchlist</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <Form.Control
            type="text"
            placeholder="Enter email address"
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            className="mb-2"
          />
          <Button
            variant="primary"
            onClick={handleAddEmail}
            disabled={!emailInput.trim()}
          >
            Add Email
          </Button>
        </div>
        {manualEmails.length > 0 && (
          <div className="mb-3">
            <h6>Recipients:</h6>
            <ul>
              {manualEmails.map((email, index) => (
                <li key={index} className="d-flex justify-content-between">
                  <span>{email}</span>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleRemoveEmail(email)}
                  >
                    Remove
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="report-info mt-3">
          <h5>Watchlist Details</h5>
          <p>
            <strong>Class:</strong> {className || "N/A"}
          </p>
          <p>
            <strong>Students:</strong> {expandedViewData?.students?.length || 0}{" "}
            included
          </p>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={isSharing}>
          Close
        </Button>
        <Button
          variant="primary"
          onClick={handleConfirmShare}
          disabled={manualEmails.length === 0 || isSharing}
        >
          {isSharing ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Sharing...
            </>
          ) : (
            `Share with ${manualEmails.length} recipient${
              manualEmails.length !== 1 ? "s" : ""
            }`
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SENShareModal;